import { Navigate, Route } from 'react-router-dom';
import {
  MONEY_PERMISSIONS,
  PURCHASES_PERMISSIONS,
  SUPPLIER_PERMISSIONS,
} from '@invento/shared';
import { PermissionGuard } from '@/navigation/guards/permission-guard';
import { ROUTE_PATHS } from '../constants';
import { lazyNamed } from '../lazy-named';
import { TenantPreviewRouteFrame } from './route-frames';
import { CompatibilityRedirect } from '../compatibility-redirect';

const loadPurchases = () => import('@/features/purchases');
const PurchaseDocumentDetailPage = lazyNamed(loadPurchases, 'PurchaseDocumentDetailPage');
const PurchaseDocumentsPage = lazyNamed(loadPurchases, 'PurchaseDocumentsPage');
const PurchaseNewBillPage = lazyNamed(loadPurchases, 'PurchaseNewBillPage');
const PurchaseNewPurchaseOrderPage = lazyNamed(loadPurchases, 'PurchaseNewPurchaseOrderPage');
const PurchaseNewSupplierPage = lazyNamed(loadPurchases, 'PurchaseNewSupplierPage');
const PurchaseSupplierDetailPage = lazyNamed(loadPurchases, 'PurchaseSupplierDetailPage');
const PurchaseSupplierEditPage = lazyNamed(loadPurchases, 'PurchaseSupplierEditPage');
const PurchaseSupplierPaymentDetailPage = lazyNamed(loadPurchases, 'PurchaseSupplierPaymentDetailPage');
const PurchaseSupplierPaymentPage = lazyNamed(loadPurchases, 'PurchaseSupplierPaymentPage');
const PurchaseSupplierReturnCreatePage = lazyNamed(loadPurchases, 'PurchaseSupplierReturnCreatePage');
const PurchaseSupplierReturnDetailPage = lazyNamed(loadPurchases, 'PurchaseSupplierReturnDetailPage');
const PurchaseSupplierReturnsPage = lazyNamed(loadPurchases, 'PurchaseSupplierReturnsPage');
const PurchaseSuppliersPage = lazyNamed(loadPurchases, 'PurchaseSuppliersPage');

export function purchasesRoutes() {
  return (
    <Route element={<TenantPreviewRouteFrame />}>
      <Route
        path="purchases"
        element={
          <PermissionGuard required={PURCHASES_PERMISSIONS.READ}>
            <CompatibilityRedirect
              to={ROUTE_PATHS.PURCHASES.DOCUMENTS}
              variant="merged"
              toLabel="Purchase documents"
            />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/documents"
        element={
          <PermissionGuard required={PURCHASES_PERMISSIONS.READ}>
            <PurchaseDocumentsPage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/suppliers"
        element={
          <PermissionGuard required={SUPPLIER_PERMISSIONS.READ}>
            <PurchaseSuppliersPage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/suppliers/new"
        element={
          <PermissionGuard required={SUPPLIER_PERMISSIONS.CREATE}>
            <PurchaseNewSupplierPage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/suppliers/:supplierId"
        element={
          <PermissionGuard required={SUPPLIER_PERMISSIONS.READ}>
            <PurchaseSupplierDetailPage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/suppliers/:supplierId/edit"
        element={
          <PermissionGuard requiredAll={[SUPPLIER_PERMISSIONS.READ, SUPPLIER_PERMISSIONS.UPDATE]}>
            <PurchaseSupplierEditPage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/documents/:documentId"
        element={
          <PermissionGuard required={PURCHASES_PERMISSIONS.READ}>
            <PurchaseDocumentDetailPage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/payments/new"
        element={
          <PermissionGuard
            requiredAll={[
              PURCHASES_PERMISSIONS.PAYMENT_CREATE,
              MONEY_PERMISSIONS.ACCOUNT_READ,
            ]}
          >
            <PurchaseSupplierPaymentPage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/payments/:paymentId"
        element={
          <PermissionGuard required={PURCHASES_PERMISSIONS.READ}>
            <PurchaseSupplierPaymentDetailPage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/returns"
        element={
          <PermissionGuard required={PURCHASES_PERMISSIONS.RETURN_READ}>
            <PurchaseSupplierReturnsPage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/returns/new"
        element={
          <PermissionGuard
            requiredAll={[
              PURCHASES_PERMISSIONS.RETURN_CREATE,
              PURCHASES_PERMISSIONS.READ,
            ]}
          >
            <PurchaseSupplierReturnCreatePage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/returns/:returnId"
        element={
          <PermissionGuard required={PURCHASES_PERMISSIONS.RETURN_READ}>
            <PurchaseSupplierReturnDetailPage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/purchase-orders/new"
        element={
          <PermissionGuard required={PURCHASES_PERMISSIONS.PO_CREATE}>
            <PurchaseNewPurchaseOrderPage />
          </PermissionGuard>
        }
      />
      <Route
        path="purchases/bills/new"
        element={
          <PermissionGuard required={PURCHASES_PERMISSIONS.BILL_CREATE}>
            <PurchaseNewBillPage />
          </PermissionGuard>
        }
      />
    </Route>
  );
}
